import { PayloadAction } from '@reduxjs/toolkit';
import { ApplicationModel } from '../../types';
import { createAppSlice } from './createAppSlice.ts';

export type ApplicationSummary = Pick<ApplicationModel, 'pk' | 'key' | 'name' | 'logo_url' | 'status'>;

/** Explicit display-only allowlist: never copy balances, permissions or credentials. */
export function applicationSummaries(value: unknown): ApplicationSummary[] {
  if (!Array.isArray(value)) return [];
  return value.filter(item => item && typeof item.pk === 'number' &&
    typeof item.key === 'string' && item.key.length <= 256 &&
    typeof item.name === 'string' && item.name.length <= 512 &&
    typeof item.status === 'string' && item.status.length <= 100).map(item => ({
    pk: item.pk, key: item.key, name: item.name, status: item.status,
    ...(typeof item.logo_url === 'string' && item.logo_url.length <= 4096 && /^(https?:\/\/|\/(?!\/))/.test(item.logo_url)
      ? {logo_url: item.logo_url} : {}),
  }));
}

export interface ApplicationSliceState {
  applications: ApplicationModel[];
  applicationsLoaded: boolean;
  application?: ApplicationModel;
  selectedApplicationKey: string;
  updatedAt: number;
  ready: boolean;
  cachedApplications: ApplicationSummary[];
  catalogUpdatedAt: number;
}

const initialState: ApplicationSliceState = {
  applications: [],
  applicationsLoaded: false,
  application: undefined,
  selectedApplicationKey: '',
  updatedAt: 0,
  ready: false,
  cachedApplications: [],
  catalogUpdatedAt: 0,
};

export const applicationSlice = createAppSlice({
  name: 'application',
  initialState,
  reducers: (create) => ({
    setApplications: create.reducer(
      (state, action: PayloadAction<ApplicationModel[]>) => {
        state.applications = action.payload.filter((a) => !!a);
        state.applicationsLoaded = true;
        state.cachedApplications = applicationSummaries(state.applications);
        state.catalogUpdatedAt = Date.now();
        if (state.application) state.application = state.applications.find(app => app.key === state.application?.key);
        if (state.selectedApplicationKey && !state.applications.some(app => app.key === state.selectedApplicationKey)) {
          state.selectedApplicationKey = '';
          state.updatedAt = Date.now();
        }
      }
    ),
    setApplication: create.reducer(
      (state, action: PayloadAction<ApplicationModel | undefined>) => {
        if (!action.payload) {
          return;
        }
        state.application = action.payload;
        state.selectedApplicationKey = action.payload.key;
        state.updatedAt = Date.now();
        const applications = state.applications.filter((a) => !!a);
        const index = applications.findIndex(
          (a) => a.pk === action.payload?.pk
        );
        if (index === -1) {
          applications.push(action.payload!);
        } else {
          applications[index] = action.payload!;
        }
        state.applications = applications;
        state.cachedApplications = state.applicationsLoaded ? applicationSummaries(applications) :
          applicationSummaries([...state.cachedApplications.filter(app => app.key !== action.payload!.key), action.payload]);
        state.catalogUpdatedAt = state.applicationsLoaded ? Date.now() : state.catalogUpdatedAt || Date.now();
      }
    ),
    clearApplication: create.reducer((state) => {
      state.application = undefined;
      state.selectedApplicationKey = '';
      state.updatedAt = Date.now();
    }),
    selectionHydrated: create.reducer(state => { state.ready = true; }),
    preferApplication: create.reducer((state, action: PayloadAction<string>) => {
      state.selectedApplicationKey = action.payload;
      state.updatedAt = Date.now();
    }),
  }),
  selectors: {
    selectApplications: (application) => application.applications,
    selectApplicationsLoaded: (application) => application.applicationsLoaded,
    selectApplication: (application) => application.application,
    selectCachedApplications: (application) => application.cachedApplications,
  },
});

export const { setApplications, setApplication, clearApplication, preferApplication } = applicationSlice.actions;

export const { selectApplications, selectApplicationsLoaded, selectApplication, selectCachedApplications } =
  applicationSlice.selectors;

/** Accept only a recent selection preference, never cached business objects. */
export function restoreApplicationSelection(value: unknown) {
  const item = value as Record<string, unknown> | null;
  const valid = item && typeof item.selectedApplicationKey === 'string' &&
    item.selectedApplicationKey.length <= 256 && typeof item.updatedAt === 'number' &&
    item.updatedAt <= Date.now() && Date.now() - item.updatedAt < 30 * 24 * 60 * 60 * 1000;
  return {
    selectedApplicationKey: valid ? item.selectedApplicationKey as string : '',
    updatedAt: valid ? item.updatedAt as number : 0,
    ready: true,
  };
}

export function restoreApplicationCatalog(value: unknown) {
  const item = value as Record<string, unknown> | null;
  const valid = item && typeof item.catalogUpdatedAt === 'number' &&
    item.catalogUpdatedAt <= Date.now() && Date.now() - item.catalogUpdatedAt < 24 * 60 * 60 * 1000;
  return {
    cachedApplications: valid ? applicationSummaries(item.cachedApplications) : [],
    catalogUpdatedAt: valid ? item.catalogUpdatedAt as number : 0,
  };
}
